import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { 
  RefreshCw,
  User,
  CheckCircle,
  Clock,
  FileText,
  AlertTriangle,
  Search,
  Wifi,
  WifiOff,
  Lock,
  Calendar,
  CreditCard,
  MapPin,
  X,
  Sparkles,
  Trash2,
  Phone,
  RotateCcw
} from 'lucide-react';

import {
  OpsBackground,
  OpsStatusBar,
  KpiTile,
  BentoSidebar,
  OpsSearchBar
} from '@/components/passport-tracker/OpsComponents';

// Helper function to format base64 image with proper data URI prefix
const formatBase64Image = (base64String) => {
  if (!base64String) return null;
  if (base64String.startsWith('data:image')) return base64String;
  return `data:image/jpeg;base64,${base64String}`;
};

// ========== ADVANCED FUZZY SEARCH SYSTEM ==========

// Normalize text for search (remove diacritics, lowercase, etc.)
const normalizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^\w\s\u0600-\u06FF]/g, '') // Keep only alphanumeric and Arabic
    .trim();
};

// Calculate Levenshtein distance for fuzzy matching
const levenshteinDistance = (str1, str2) => {
  const m = str1.length;
  const n = str2.length;
  const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
      }
    }
  }
  return dp[m][n];
};

// Calculate similarity score (0-100)
const calculateSimilarity = (str1, str2) => {
  if (!str1 || !str2) return 0;
  const norm1 = normalizeText(str1);
  const norm2 = normalizeText(str2);
  if (!norm1 || !norm2) return 0;
  
  const maxLen = Math.max(norm1.length, norm2.length);
  if (maxLen === 0) return 100;
  
  const distance = levenshteinDistance(norm1, norm2);
  return Math.round((1 - distance / maxLen) * 100);
};

// Check if query matches any part of the text
const fuzzyMatch = (text, query, threshold = 60) => {
  if (!text || !query) return { match: false, score: 0, matchedPart: '' };
  
  const normText = normalizeText(text);
  const normQuery = normalizeText(query);
  
  if (!normText || !normQuery) return { match: false, score: 0, matchedPart: '' };
  
  // Exact match
  if (normText.includes(normQuery)) {
    return { match: true, score: 100, matchedPart: query };
  }
  
  // Check each word
  const textWords = normText.split(/\s+/);
  const queryWords = normQuery.split(/\s+/);
  
  let totalScore = 0;
  let matchedWords = 0;
  
  for (const qWord of queryWords) {
    let bestWordScore = 0;
    for (const tWord of textWords) {
      // Starts with match (high priority)
      if (tWord.startsWith(qWord)) {
        bestWordScore = Math.max(bestWordScore, 95);
      }
      // Contains match
      else if (tWord.includes(qWord)) {
        bestWordScore = Math.max(bestWordScore, 85);
      }
      // Fuzzy match
      else {
        const similarity = calculateSimilarity(qWord, tWord);
        if (similarity >= threshold) {
          bestWordScore = Math.max(bestWordScore, similarity);
        }
      }
    }
    if (bestWordScore > 0) {
      totalScore += bestWordScore;
      matchedWords++;
    }
  }
  
  const finalScore = queryWords.length > 0 ? Math.round(totalScore / queryWords.length) : 0;
  
  return {
    match: matchedWords > 0 && finalScore >= threshold,
    score: finalScore,
    matchedPart: query
  };
};

// Advanced search function
const advancedSearch = (items, query, searchFields) => {
  if (!query || !query.trim()) return items.map(item => ({ ...item, searchScore: 0 }));
  
  const results = items.map(item => {
    let maxScore = 0;
    let matchedField = '';
    
    for (const field of searchFields) {
      const value = item[field.key];
      if (!value) continue;
      
      const result = fuzzyMatch(value, query, field.threshold || 50);
      const weightedScore = result.score * (field.weight || 1);
      
      if (result.match && weightedScore > maxScore) {
        maxScore = weightedScore;
        matchedField = field.key;
      }
    }
    
    return {
      ...item,
      searchScore: maxScore,
      matchedField
    };
  });
  
  return results
    .filter(item => item.searchScore > 0)
    .sort((a, b) => b.searchScore - a.searchScore);
};

// Highlight matched text
const highlightMatch = (text, query) => {
  if (!text || !query) return text;
  
  const normQuery = normalizeText(query);
  const words = normQuery.split(/\s+/).filter(w => w.length > 0);
  
  if (words.length === 0) return text;
  
  let result = text;
  for (const word of words) {
    const regex = new RegExp(`(${word})`, 'gi');
    result = result.replace(regex, '|||$1|||');
  }
  
  return result.split('|||').map((part, index) => {
    const isMatch = words.some(w => normalizeText(part) === w);
    if (isMatch) {
      return <span key={index} className="bg-yellow-500/40 text-yellow-200 px-0.5 rounded">{part}</span>;
    }
    return part;
  });
};

// ========== END SEARCH SYSTEM ==========

// Calculate days since entry
const calculateDaysSince = (entryDate) => {
  if (!entryDate) return null;
  
  let entry;
  if (entryDate.includes('/')) {
    const parts = entryDate.split('/');
    if (parts[2].length === 4) {
      entry = new Date(parts[2], parts[1] - 1, parts[0]);
    } else {
      entry = new Date(parts[0], parts[1] - 1, parts[2]);
    }
  } else {
    entry = new Date(entryDate);
  }
  
  if (isNaN(entry.getTime())) return null;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  entry.setHours(0, 0, 0, 0);
  const diffTime = today - entry;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Get status config based on passport state
const getPassportStatus = (passport, currentTab) => {
  // Sent passports
  if (currentTab === 'sent' || passport.passportSent) {
    return {
      status: 'sent',
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/15',
      borderColor: 'border-blue-500',
      cardBg: 'bg-gradient-to-br from-blue-900/30 to-blue-950/50',
      text: 'SENT',
      textAr: 'تم الإرسال'
    };
  }
  
  // Ready to send (has contract)
  if (passport.has_contract) {
    return {
      status: 'ready',
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/15',
      borderColor: 'border-emerald-500',
      cardBg: 'bg-gradient-to-br from-emerald-900/30 to-emerald-950/50',
      text: 'READY',
      textAr: 'جاهز للإرسال'
    };
  }

  // Refund alert: contract was active and is now refunded — needs attention
  if (passport.refund_alert) {
    return {
      status: 'refund',
      color: 'text-amber-300',
      bgColor: 'bg-amber-500/15',
      borderColor: 'border-amber-500/70',
      cardBg: 'bg-gradient-to-br from-amber-900/30 to-[#0F172A]',
      text: 'REFUNDED',
      textAr: 'تم الاسترجاع'
    };
  }
  
  // Pending (no contract) - check urgency
  const daysSince = passport.daysSinceEntry || calculateDaysSince(passport.entry_date) || 0;
  
  if (daysSince > 30) {
    return {
      status: 'critical',
      color: 'text-red-400',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500',
      cardBg: 'bg-gradient-to-br from-red-900/20 to-[#0F172A]',
      text: 'CRITICAL',
      textAr: 'حرج'
    };
  }
  
  if (daysSince > 14) {
    return {
      status: 'high',
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/50',
      cardBg: 'bg-gradient-to-br from-orange-900/20 to-[#0F172A]',
      text: 'HIGH PRIORITY',
      textAr: 'أولوية عالية'
    };
  }
  
  if (daysSince > 7) {
    return {
      status: 'medium',
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/10',
      borderColor: 'border-amber-500/50',
      cardBg: 'bg-gradient-to-br from-amber-900/20 to-[#0F172A]',
      text: 'PENDING',
      textAr: 'معلق'
    };
  }
  
  return {
    status: 'normal',
    color: 'text-slate-400',
    bgColor: 'bg-slate-500/10',
    borderColor: 'border-slate-500/30',
    cardBg: 'bg-gradient-to-br from-[#1E293B] to-[#0F172A]',
    text: 'PENDING',
    textAr: 'معلق'
  };
};

// PIN Screen Component
const PinScreen = ({ onUnlock }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/passport-tracker/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin })
      });
      const data = await response.json();
      
      if (data.valid) {
        sessionStorage.setItem('passportTrackerPin', pin);
        onUnlock();
      } else {
        setError(true);
        setTimeout(() => setError(false), 2000);
        setPin('');
      }
    } catch (err) {
      // Fallback to hardcoded PIN for testing
      if (pin === '123456') {
        sessionStorage.setItem('passportTrackerPin', pin);
        onUnlock();
      } else {
        setError(true);
        setTimeout(() => setError(false), 2000);
        setPin('');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#000716] to-[#0a1628] flex flex-col items-center justify-center p-4">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-white tracking-widest mb-8">TADABER</h1>
        <div className="flex items-center justify-center gap-2 mb-6">
          <Lock className="h-5 w-5 text-white/60" />
          <p className="text-white/60">Enter PIN</p>
        </div>
        
        <Input
          type="password"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          placeholder="••••••"
          maxLength={6}
          className="w-72 h-14 text-center text-2xl tracking-[8px] bg-white/10 border-2 border-white/20 focus:border-[#F6C13B] text-white mb-4"
          data-testid="pin-input"
        />
        
        <Button
          onClick={handleSubmit}
          disabled={loading}
          className="w-72 h-12 bg-gradient-to-r from-[#F6C13B] to-[#E07B27] hover:opacity-90 text-white font-bold"
          data-testid="pin-submit-btn"
        >
          {loading ? 'Verifying...' : 'Enter'}
        </Button>
        
        {error && (
          <p className="text-red-400 text-sm mt-4 animate-pulse">
            Wrong PIN
          </p>
        )}
      </div>
    </div>
  );
};

// Main Passport Tracker Component
const PassportTrackerPage = () => {
  const [unlocked, setUnlocked] = useState(true); // Auto-unlock for now
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [currentTab, setCurrentTab] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [accountingStatus, setAccountingStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Check if already unlocked
  useEffect(() => {
    const savedPin = sessionStorage.getItem('passportTrackerPin');
    if (savedPin) {
      setUnlocked(true);
    }
  }, []);

  // Load data from API
  const loadData = useCallback(async (forceRefresh = false) => {
    try {
      setRefreshing(true);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/passports/all${forceRefresh ? '?force_refresh=true' : ''}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to load');
      const result = await response.json();
      setData(result);
      setAccountingStatus(result.accounting_hub);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error loading passport data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (unlocked) {
      loadData();
      const interval = setInterval(() => loadData(), 30000);
      return () => clearInterval(interval);
    }
  }, [unlocked, loadData]);

  // Mark passport as sent
  const markAsSent = async (visaId, method, notes = '') => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/visas/${visaId}/passport-sent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          passportSent: true,
          passportSentDate: new Date().toISOString().split('T')[0],
          passportSentBy: 'System',
          passportSentMethod: method,
          passportSentNotes: notes
        })
      });
      loadData(true);
    } catch (error) {
      console.error('Error marking passport as sent:', error);
    }
  };

  // Advanced search configuration
  const searchFields = useMemo(() => [
    { key: 'passenger_name', weight: 1.5, threshold: 40 },  // Highest priority
    { key: 'customer_name', weight: 1.2, threshold: 50 },   // Sponsor name
    { key: 'passport_number', weight: 1.0, threshold: 70 }, // Exact match preferred
    { key: 'contract_number', weight: 0.8, threshold: 80 }  // Contract number
  ], []);

  // Filter list by search and tab with advanced fuzzy search
  const getFilteredList = useCallback(() => {
    if (!data) return [];
    
    let list;
    if (currentTab === 'pending') {
      list = data.pending.filter(v => !v.has_contract);
    } else if (currentTab === 'ready') {
      list = data.pending.filter(v => v.has_contract);
    } else if (currentTab === 'contracts') {
      list = data.contracts_need_action || [];
    } else {
      list = data.sent;
    }
    
    if (!searchQuery || !searchQuery.trim()) return list;
    
    // Use advanced fuzzy search
    return advancedSearch(list, searchQuery, searchFields);
  }, [data, currentTab, searchQuery, searchFields]);

  const filteredList = getFilteredList();

  // Stats
  const stats = data ? {
    pending: data.stats?.no_contract || 0,
    ready: data.stats?.has_contract_ready || 0,
    sent: data.stats?.sent_count || 0,
    contractsNeedAction: data.stats?.contracts_need_action_count || 0,
    refundAlert: data.stats?.refund_alert_count || 0
  } : { pending: 0, ready: 0, sent: 0, contractsNeedAction: 0, refundAlert: 0 };

  // Handle dismissing a contract (admin only)
  const dismissContract = async (passportNumber) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا العقد؟\nAre you sure you want to dismiss this contract?')) {
      return;
    }
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contracts/dismiss/${passportNumber}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      loadData(true);
    } catch (error) {
      console.error('Error dismissing contract:', error);
    }
  };

  // Handle marking passport as received
  const markPassportReceived = async (contract) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contracts/passport-received/${contract.passport_number}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          worker_name: contract.worker_name,
          nationality: contract.worker_nationality
        })
      });
      loadData(true);
    } catch (error) {
      console.error('Error marking passport as received:', error);
    }
  };

  if (!unlocked) {
    return <PinScreen onUnlock={() => setUnlocked(true)} />;
  }

  // FIDS sample for top scrolling strip
  const fidsSample = (data?.pending || []).slice(0, 8).map((p) => ({
    flag: p.nationality?.includes('PHIL') ? '🇵🇭' : p.nationality?.includes('INDO') ? '🇮🇩' : p.nationality?.includes('KEN') ? '🇰🇪' : p.nationality?.includes('BANG') ? '🇧🇩' : '🌍',
    name: (p.passenger_name || '').slice(0, 22),
    passport: p.passport_number || '—',
    status: p.has_contract ? 'READY' : p.refund_alert ? 'REFUNDED' : 'PENDING',
    statusColor: p.has_contract ? 'text-emerald-400' : p.refund_alert ? 'text-amber-300' : 'text-red-400',
    extra: p.refund_alert ? `#${p.refunded_contract_number}` : p.has_contract ? `#${p.contract_number}` : `${p.daysSinceEntry || 0}d`,
  }));

  return (
    <OpsBackground>
      {/* OPS STATUS BAR */}
      <OpsStatusBar
        accountingStatus={accountingStatus}
        stats={stats}
        refreshing={refreshing}
        onRefresh={() => loadData(true)}
        lastUpdate={lastUpdate}
        sample={fidsSample}
      />

      <div className="px-4 sm:px-6 py-4 sm:py-6">
        {/* Page Title */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-black tracking-[0.2em] uppercase text-white" style={{ fontFamily: 'ui-monospace, "JetBrains Mono", "IBM Plex Mono", monospace' }}>
              <span className="text-amber-400">/</span> PASSPORTS
            </h1>
            <p className="text-[11px] font-mono tracking-wider text-white/40 uppercase">Passport Tracker · متابعة الجوازات</p>
          </div>
          {lastUpdate && (
            <div className="text-right text-[10px] font-mono text-white/40">
              <div className="text-white/60">LAST UPDATE</div>
              <div className="text-amber-300">{lastUpdate.toLocaleTimeString('en-GB', { hour12: false })}</div>
            </div>
          )}
        </div>

        {/* KPI HERO STRIP */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-4">
          <KpiTile
            testId="kpi-pending"
            label="PENDING"
            labelAr="لم يتم التسديد"
            accent="NO CONTRACT"
            value={stats.pending}
            color="red"
            icon={Clock}
            sparkline={[2, 3, 4, 3, 5, 6, 7, 8, 9, 10]}
            active={currentTab === 'pending'}
            onClick={() => setCurrentTab('pending')}
          />
          <KpiTile
            testId="kpi-ready"
            label="READY TO SEND"
            labelAr="جاهز للإرسال"
            accent="PAID"
            value={stats.ready}
            color="emerald"
            icon={CheckCircle}
            sparkline={[1, 2, 3, 4, 5, 7, 8, 9, 11, 12]}
            active={currentTab === 'ready'}
            onClick={() => setCurrentTab('ready')}
          />
          <KpiTile
            testId="kpi-refund"
            label="REFUND ALERT"
            labelAr="مرتجع"
            accent="WAS READY"
            value={stats.refundAlert}
            color="amber"
            icon={RotateCcw}
            sparkline={[0, 1, 0, 1, 2, 1, 2, 3]}
            active={false}
            onClick={() => setCurrentTab('pending')}
          />
          <KpiTile
            testId="kpi-sent"
            label="SENT TODAY"
            labelAr="تم الإرسال"
            accent="DELIVERED"
            value={stats.sent}
            color="blue"
            icon={CheckCircle}
            sparkline={[5, 6, 8, 7, 10, 11, 13, 14, 16, 18]}
            active={currentTab === 'sent'}
            onClick={() => setCurrentTab('sent')}
          />
        </div>

        {/* Layout: Main + Bento Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
          {/* MAIN COLUMN */}
          <div>
            {/* Tabs + Search */}
            <div className="mb-3 space-y-3">
              <OpsSearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                count={filteredList.length}
                bestScore={filteredList[0]?.searchScore || 0}
              />
              {/* Tab navigation */}
              <div className="flex flex-wrap gap-2 text-[11px] font-mono">
                {[
                  { id: 'pending', label: 'PENDING', count: stats.pending, color: 'red' },
                  { id: 'ready', label: 'READY', count: stats.ready, color: 'emerald' },
                  { id: 'sent', label: 'SENT', count: stats.sent, color: 'blue' },
                  { id: 'contracts', label: 'CONTRACTS', count: stats.contractsNeedAction, color: 'amber' },
                ].map((t) => {
                  const cls = currentTab === t.id
                    ? `bg-${t.color}-500/20 text-${t.color}-300 border-${t.color}-500/50`
                    : 'bg-white/5 text-white/50 border-white/10';
                  return (
                    <button
                      key={t.id}
                      onClick={() => setCurrentTab(t.id)}
                      className={`px-3 h-8 rounded-lg border tracking-widest uppercase transition ${cls}`}
                      data-testid={`tab-${t.id}`}
                    >
                      {t.label} <span className="opacity-60">· {t.count}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content */}
            {loading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-64 rounded-2xl bg-white/5" />
                ))}
              </div>
            ) : filteredList.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-10 text-center">
                <div className="text-3xl mb-3">{searchQuery ? '🔍' : currentTab === 'pending' ? '✅' : '📭'}</div>
                <p className="text-white/50 font-mono text-sm tracking-wider">
                  {searchQuery
                    ? 'NO RESULTS · لا توجد نتائج'
                    : currentTab === 'pending' ? 'ALL CLEAR · لا توجد جوازات معلقة'
                    : currentTab === 'ready' ? 'NONE READY · لا توجد جوازات جاهزة'
                    : currentTab === 'sent' ? 'NO SENT · لا توجد جوازات مرسلة'
                    : 'NO CONTRACTS · لا توجد عقود'}
                </p>
              </div>
            ) : currentTab === 'contracts' ? (
              <div className="space-y-3">
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
                  <div className="flex items-center gap-2 text-amber-300 text-[11px] font-mono tracking-widest uppercase">
                    <AlertTriangle className="h-4 w-4" />
                    Contracts Need Action · عقود تحتاج إجراء
                  </div>
                  <p className="text-amber-400/60 text-[10px] mt-1">From Tadaber, no passport collected yet</p>
                </div>
                {filteredList.map((contract) => (
                  <ContractCard
                    key={contract.passport_number}
                    contract={contract}
                    onDismiss={dismissContract}
                    onPassportReceived={markPassportReceived}
                    searchQuery={searchQuery}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredList.map((passport) => (
                  <PassportCard
                    key={passport.id}
                    passport={passport}
                    currentTab={currentTab}
                    onMarkSent={markAsSent}
                    searchQuery={searchQuery}
                  />
                ))}
              </div>
            )}
          </div>

          {/* BENTO SIDEBAR (desktop only) */}
          <aside className="hidden lg:block">
            <BentoSidebar data={data} />
          </aside>
        </div>
      </div>
    </OpsBackground>
  );
};


// Passport Card Component - MATCHING VisaTrackerPage design
const PassportCard = ({ passport, currentTab, onMarkSent, searchQuery }) => {
  const [showSendOptions, setShowSendOptions] = useState(false);
  const daysSince = passport.daysSinceEntry || calculateDaysSince(passport.entry_date) || 0;
  const statusConfig = getPassportStatus(passport, currentTab);
  const StatusIcon = statusConfig.status === 'sent' ? CheckCircle : 
                     statusConfig.status === 'ready' ? CheckCircle : 
                     statusConfig.status === 'critical' ? AlertTriangle : Clock;

  // Get initials for photo placeholder
  const initials = (passport.passenger_name || 'UN').split(' ').map(n => n[0]).join('').substring(0,2).toUpperCase();

  const isPending = currentTab === 'pending';
  const isReady = currentTab === 'ready';
  const isSent = currentTab === 'sent';
  
  // Highlight matched text if searching
  const renderHighlightedText = (text) => {
    if (!searchQuery || !text) return text;
    return highlightMatch(text, searchQuery);
  };

  return (
    <Card className={`relative rounded-2xl overflow-visible border-2 ${statusConfig.borderColor} ${statusConfig.cardBg}`}>
      {/* Status Header */}
      <div className={`${statusConfig.bgColor} border-b ${statusConfig.borderColor} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <StatusIcon className={`h-4 w-4 ${statusConfig.color}`} />
          <span className={`${statusConfig.color} text-xs font-bold`}>
            {statusConfig.text}
          </span>
        </div>
        <span className="text-white/50 text-xs font-mono">
          #{(passport.passport_number || passport.id || '').substring(0, 12).toUpperCase()}
        </span>
      </div>

      {/* Alert Banner */}
      {isSent && (
        <div className="bg-blue-600 text-center py-2">
          <span className="text-white text-xs font-bold">✓ PASSPORT SENT - {statusConfig.textAr}</span>
        </div>
      )}
      {isReady && (
        <div className="bg-emerald-600 text-center py-2">
          <span className="text-white text-xs font-bold">✓ READY TO SEND - جاهز للإرسال</span>
        </div>
      )}
      {isPending && statusConfig.status === 'critical' && (
        <div className="bg-red-600 text-center py-2">
          <span className="text-white text-xs font-bold">🚨 CRITICAL - أكثر من 30 يوم!</span>
        </div>
      )}
      {isPending && statusConfig.status === 'high' && (
        <div className="bg-orange-600 text-center py-2">
          <span className="text-white text-xs font-bold">⚠️ HIGH PRIORITY - أكثر من 14 يوم</span>
        </div>
      )}

      {/* Main Content */}
      <CardContent className={`p-4 relative ${isSent ? 'opacity-80' : ''}`}>
        {/* Sent Stamp - Rectangular */}
        {isSent && (
          <div className="mb-3 flex justify-center">
            <div className="border-4 border-blue-500 rounded-lg px-6 py-2.5 bg-blue-900/95 shadow-lg shadow-blue-500/30">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-blue-400" />
                <div className="flex flex-col items-start">
                  <span className="text-blue-300 text-sm font-black tracking-wider">PASSPORT SENT</span>
                  <span className="text-blue-300 text-xs font-bold tracking-wider">تم إرسال الجواز</span>
                  <span className="text-blue-200 text-[10px] font-medium mt-0.5">
                    {passport.passportSentDate 
                      ? new Date(passport.passportSentDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase() 
                      : new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Person Info Row */}
        <div className="flex items-start gap-3 mb-4">
          <div className={`w-14 h-14 rounded-xl ${statusConfig.bgColor} border ${statusConfig.borderColor} flex items-center justify-center overflow-hidden flex-shrink-0`}>
            {passport.passenger_photo ? (
              <img src={formatBase64Image(passport.passenger_photo)} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-[#F6C13B] font-bold text-base">{initials}</span>
            )}
          </div>
          <div className="flex-1">
            {/* Worker Name - Full and Clear */}
            <h3 className="font-bold text-white text-base leading-tight mb-1">
              {renderHighlightedText(passport.passenger_name || 'Unknown')}
            </h3>
            <div className="flex flex-wrap items-center gap-2 text-xs text-white/50">
              {passport.passport_number && (
                <span className="bg-white/10 px-2 py-0.5 rounded font-mono">
                  {renderHighlightedText(passport.passport_number)}
                </span>
              )}
              {passport.nationality && <span>{passport.nationality}</span>}
            </div>
            {/* Search Score Badge */}
            {searchQuery && passport.searchScore > 0 && (
              <div className="mt-1">
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                  passport.searchScore >= 90 ? 'bg-green-500/20 text-green-400' :
                  passport.searchScore >= 70 ? 'bg-blue-500/20 text-blue-400' :
                  'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {passport.searchScore >= 90 ? '⭐ Exact Match' : 
                   passport.searchScore >= 70 ? '✓ Good Match' : 
                   '~ Partial Match'} ({Math.round(passport.searchScore)}%)
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="bg-[#0F172A]/50 rounded-lg p-3">
            <div className="text-[10px] text-white/50 mb-1 flex items-center gap-1">
              <CreditCard className="h-3 w-3" />
              رقم الجواز / Passport
            </div>
            <div className="text-sm font-medium text-white font-mono">{passport.passport_number || 'N/A'}</div>
          </div>
          <div className="bg-[#0F172A]/50 rounded-lg p-3">
            <div className="text-[10px] text-white/50 mb-1 flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              تاريخ الدخول / Entry
            </div>
            <div className="text-sm font-medium text-white">{passport.entry_date || 'N/A'}</div>
          </div>
          <div className="bg-[#0F172A]/50 rounded-lg p-3">
            <div className="text-[10px] text-white/50 mb-1">Visa Type</div>
            <div className="text-sm font-medium text-white">{passport.visa_type || 'Work'}</div>
          </div>
          <div className="bg-[#0F172A]/50 rounded-lg p-3">
            <div className="text-[10px] text-white/50 mb-1 flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              Country
            </div>
            <div className="text-sm font-medium text-white">{passport.visa_country || 'UAE'}</div>
          </div>
        </div>

        {/* Sponsor Info */}
        {(passport.sponsor_name || passport.customer_name) && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-3">
            <div className="text-[10px] text-blue-400/70 mb-1 flex items-center gap-1">
              <User className="h-3 w-3" />
              Sponsor / الكفيل
            </div>
            <div className="text-sm text-blue-300 font-medium">
              {renderHighlightedText(passport.sponsor_name || passport.customer_name)}
            </div>
            {/* Phone Number */}
            {(passport.sponsor_phone || passport.customer_phone) && (
              <div className="mt-2 pt-2 border-t border-blue-500/20 flex items-center gap-2">
                <span className="text-blue-400/60 text-xs">📞 Phone / الهاتف:</span>
                <a 
                  href={`tel:${passport.sponsor_phone || passport.customer_phone}`}
                  className="text-blue-300 text-sm font-mono hover:text-blue-200"
                >
                  {passport.sponsor_phone || passport.customer_phone}
                </a>
              </div>
            )}
          </div>
        )}

        {/* Days Counter */}
        <div className={`text-center py-3 rounded-xl ${statusConfig.bgColor} border ${statusConfig.borderColor} mb-3`}>
          <div className={`text-3xl font-bold ${statusConfig.color}`}>{daysSince}</div>
          <div className="text-[10px] text-white/50">days since entry / يوم منذ الدخول</div>
        </div>

        {/* Contract Status Section */}
        {!isSent && (
          <div className={`rounded-xl p-3 mb-3 ${
            passport.has_contract 
              ? 'bg-emerald-500/10 border-2 border-emerald-500' 
              : passport.refund_alert
                ? 'bg-amber-500/10 border-2 border-amber-500/70'
                : 'bg-red-500/10 border-2 border-dashed border-red-500/50'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  passport.has_contract ? 'bg-emerald-500/20' : passport.refund_alert ? 'bg-amber-500/20' : 'bg-red-500/20'
                }`}>
                  {passport.has_contract ? (
                    <CheckCircle className="h-5 w-5 text-emerald-400" />
                  ) : passport.refund_alert ? (
                    <RotateCcw className="h-5 w-5 text-amber-400" />
                  ) : (
                    <Clock className="h-5 w-5 text-red-400" />
                  )}
                </div>
                <div>
                  <p className={`font-bold text-sm ${
                    passport.has_contract ? 'text-emerald-400' : passport.refund_alert ? 'text-amber-400' : 'text-red-400'
                  }`}>
                    {passport.has_contract
                      ? 'تم التسديد - جاهز للإرسال'
                      : passport.refund_alert
                        ? 'تم استرجاع العقد'
                        : 'معلق - لم يتم التسديد'}
                  </p>
                  <p className={`text-xs ${
                    passport.has_contract ? 'text-emerald-400/60' : passport.refund_alert ? 'text-amber-400/70' : 'text-red-400/60'
                  }`}>
                    {passport.has_contract
                      ? 'Paid - Ready to Send'
                      : passport.refund_alert
                        ? 'Contract Refunded - Was Ready'
                        : 'Pending - No Contract'}
                  </p>
                </div>
              </div>
              <div className={`px-3 py-1.5 rounded-full border ${
                passport.has_contract 
                  ? 'bg-emerald-500/20 border-emerald-500/40' 
                  : passport.refund_alert
                    ? 'bg-amber-500/20 border-amber-500/50'
                    : 'bg-red-500/20 border-red-500/40'
              }`}>
                <span className={`text-xs font-bold ${
                  passport.has_contract ? 'text-emerald-400' : passport.refund_alert ? 'text-amber-300' : 'text-red-400'
                }`}>
                  {passport.has_contract ? '✅ جاهز' : passport.refund_alert ? '⚠ مرتجع' : '❌ لا يمكن الإرسال'}
                </span>
              </div>
            </div>
            {/* Refund details */}
            {!passport.has_contract && passport.refund_alert && (
              <div className="mt-3 pt-3 border-t border-amber-500/30 space-y-1.5 text-xs">
                {passport.refunded_contract_number && (
                  <div className="flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5 text-amber-400/60" />
                    <span className="text-amber-300/70">عقد سابق / Previous contract:</span>
                    <span className="text-amber-200 font-mono font-bold">#{passport.refunded_contract_number}</span>
                  </div>
                )}
                {passport.refunded_customer_name && (
                  <div className="flex items-center gap-2">
                    <User className="h-3.5 w-3.5 text-amber-400/60" />
                    <span className="text-amber-300/70">عميل سابق / Previous client:</span>
                    <span className="text-amber-100 truncate">{passport.refunded_customer_name}</span>
                  </div>
                )}
                {passport.refunded_at && (
                  <div className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5 text-amber-400/60" />
                    <span className="text-amber-300/70">تاريخ الاسترجاع / Refunded:</span>
                    <span className="text-amber-100">
                      {new Date(passport.refunded_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                )}
              </div>
            )}
            {/* Contract Number */}
            {passport.has_contract && passport.contract_number && (
              <div className="mt-3 pt-3 border-t border-emerald-500/20">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-emerald-400/50" />
                  <span className="text-emerald-400/70 text-xs">رقم العقد / Contract:</span>
                  <span className="text-emerald-300 text-sm font-mono font-bold">{passport.contract_number}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sent Info */}
        {isSent && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 mb-3">
            <div className="flex items-center gap-2 text-blue-400 text-sm font-bold mb-2">
              <CheckCircle className="h-4 w-4" />
              Sent Successfully / تم الإرسال بنجاح
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {passport.passportSentDate && (
                <div>
                  <p className="text-white/40">Date / التاريخ</p>
                  <p className="text-white">{passport.passportSentDate}</p>
                </div>
              )}
              {passport.passportSentMethod && (
                <div>
                  <p className="text-white/40">Method / الطريقة</p>
                  <p className="text-white">{passport.passportSentMethod}</p>
                </div>
              )}
            </div>
            {/* Contract Number Only - Sponsor info already shown above */}
            {passport.contract_number && (
              <div className="mt-3 pt-3 border-t border-blue-500/20">
                <div className="flex items-center gap-2 text-xs">
                  <FileText className="h-3 w-3 text-blue-400/50" />
                  <span className="text-white/50">Contract / رقم العقد:</span>
                  <span className="text-blue-300 font-mono font-bold">{passport.contract_number}</span>
                </div>
              </div>
            )}
            {passport.passportSentNotes && (
              <p className="text-white/40 text-xs mt-2">
                Notes / ملاحظات: <span className="text-white">{passport.passportSentNotes}</span>
              </p>
            )}
          </div>
        )}

        {/* Send Actions (for ready tab only) */}
        {isReady && (
          <div className="mt-3">
            {!showSendOptions ? (
              <Button
                onClick={() => setShowSendOptions(true)}
                className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                إرسال الجواز / Send Passport
              </Button>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-white/60 text-center mb-2">اختر طريقة الإرسال / Choose method:</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    onClick={() => {
                      onMarkSent(passport.id, 'Personal Pickup', '');
                      setShowSendOptions(false);
                    }}
                    variant="outline"
                    size="sm"
                    className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                  >
                    🤝 استلام شخصي
                  </Button>
                  <Button
                    onClick={() => {
                      onMarkSent(passport.id, 'Courier', '');
                      setShowSendOptions(false);
                    }}
                    variant="outline"
                    size="sm"
                    className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                  >
                    🚚 مندوب
                  </Button>
                  <Button
                    onClick={() => {
                      onMarkSent(passport.id, 'Shipping', '');
                      setShowSendOptions(false);
                    }}
                    variant="outline"
                    size="sm"
                    className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                  >
                    📦 شحن
                  </Button>
                  <Button
                    onClick={() => setShowSendOptions(false)}
                    variant="outline"
                    size="sm"
                    className="border-white/20 text-white/60"
                  >
                    ❌ إلغاء
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Contract Card Component for contracts that need action
const ContractCard = ({ contract, onDismiss, onPassportReceived, searchQuery }) => {
  // Highlight matched text if searching
  const renderHighlightedText = (text) => {
    if (!searchQuery || !text) return text;
    return highlightMatch(text, searchQuery);
  };

  return (
    <Card className="relative rounded-2xl overflow-visible border-2 border-amber-500/50 bg-gradient-to-br from-amber-900/20 to-[#0F172A]">
      {/* Status Header */}
      <div className="bg-amber-500/15 border-b border-amber-500/30 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-400" />
          <span className="text-amber-400 text-xs font-bold">
            عقد يحتاج إجراء / CONTRACT NEEDS ACTION
          </span>
        </div>
        <span className="text-white/50 text-xs font-mono">
          #{contract.contract_number}
        </span>
      </div>

      {/* Alert Banner - Bilingual */}
      <div className="bg-amber-600 text-center py-2">
        <span className="text-white text-xs font-bold">
          ⚠️ الجواز يجب استلامه من العميل السابق / Passport must be collected from previous client
        </span>
      </div>

      <CardContent className="p-4">
        {/* Worker Info */}
        <div className="flex items-start gap-4 mb-4">
          <div className="w-16 h-16 rounded-xl bg-amber-500/20 border-2 border-amber-500/40 flex items-center justify-center">
            <User className="h-8 w-8 text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-bold text-lg truncate">
              {renderHighlightedText(contract.worker_name || 'غير معروف / Unknown')}
            </h3>
            <p className="text-amber-400/70 text-xs">
              {contract.worker_nationality || 'غير محدد / Not specified'}
            </p>
            <p className="text-white/40 text-xs mt-1">
              جواز / Passport: {contract.passport_number}
            </p>
          </div>
        </div>

        {/* Customer Info */}
        <div className="bg-amber-500/10 rounded-xl p-3 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <User className="h-4 w-4 text-amber-400" />
            <span className="text-amber-400 text-xs font-bold">العميل الجديد / New Customer</span>
          </div>
          <p className="text-white font-medium">
            {renderHighlightedText(contract.customer_name)}
          </p>
          {contract.customer_phone && (
            <div className="flex items-center gap-2 mt-2">
              <Phone className="h-3 w-3 text-amber-400/60" />
              <span className="text-amber-400/80 text-sm" dir="ltr">{contract.customer_phone}</span>
            </div>
          )}
        </div>

        {/* Payment Info - Bilingual */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="bg-[#1E293B]/50 rounded-lg p-2 text-center">
            <p className="text-white/40 text-[10px]">إجمالي العقد / Total</p>
            <p className="text-white font-bold text-sm">{contract.total_amount?.toLocaleString() || 0} AED</p>
          </div>
          <div className="bg-[#1E293B]/50 rounded-lg p-2 text-center">
            <p className="text-white/40 text-[10px]">المتبقي / Remaining</p>
            <p className={`font-bold text-sm ${contract.remaining_amount > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
              {contract.remaining_amount?.toLocaleString() || 0} AED
            </p>
          </div>
        </div>

        {/* Action Buttons - Bilingual */}
        <div className="flex gap-2">
          <Button
            onClick={() => onPassportReceived(contract)}
            className="flex-1 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white font-bold text-xs"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            تم استلام الجواز / Received
          </Button>
          <Button
            onClick={() => onDismiss(contract.passport_number)}
            variant="outline"
            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
            title="حذف / Delete"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PassportTrackerPage;
